import React, { useState, useEffect } from 'react';
import { Camera, Heart, Upload, Sparkles, Eye } from 'lucide-react';

import { PhotoUpload } from '../components/PhotoUpload';
import { FloralCard } from '../components/ui/FloralCard';

interface PhotoUploadPageProps {
  eventType?: string;
}

const PhotoUploadPage: React.FC<PhotoUploadPageProps> = () => {
  const [eventType, setEventType] = useState<string>('');

  useEffect(() => {
    // Get event type from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const event = urlParams.get('event') || 'general';
    setEventType(event);
  }, []);

  const getEventTitle = (event: string) => {
    switch (event) {
      case 'ceremony':
        return 'Ceremony Photos';
      case 'reception':
        return 'Reception Photos';
      case 'candid':
        return 'Candid Moments';
      default:
        return 'Wedding Photos';
    }
  };

  const getEventDescription = (event: string) => {
    switch (event) {
      case 'ceremony':
        return 'Share your beautiful moments from the wedding ceremony';
      case 'reception':
        return 'Upload your favorite shots from the reception celebration';
      case 'candid':
        return 'Capture those spontaneous, heartfelt moments';
      default:
        return 'Share your favorite moments from our special day';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-cream-50 to-dusty-rose-50">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <FloralCard variant="elevated" padding="lg" className="text-center mb-6 sm:mb-8" showCornerDecorations={false} showWatercolorBackground={false}>
            <div className="flex justify-center mb-4">
              <div className="p-3 sm:p-4 bg-sage-100 rounded-full">
                <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-sage-600" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 font-serif">
              {getEventTitle(eventType)}
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-4 px-4">
              {getEventDescription(eventType)}
            </p>
            <div className="flex items-center justify-center text-sage-600">
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 mr-2 fill-current" />
              <span className="text-sm font-medium">Thank you for celebrating with us!</span>
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 ml-2 fill-current" />
            </div>
            <div className="mt-4">
              <a
                href="/gallery"
                className="inline-flex items-center space-x-2 bg-sage-600 text-white px-4 py-2 rounded-lg hover:bg-sage-700 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span>View Gallery</span>
              </a>
            </div>
          </FloralCard>

          {/* Upload Component */}
          <FloralCard variant="elevated" padding="md" className="overflow-hidden" showCornerDecorations={false} showWatercolorBackground={false}>
            <div className="bg-gradient-to-r from-sage-500 to-sage-600 p-6 text-white rounded-lg mb-6">
              <div className="flex items-center justify-center">
                <Upload className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-semibold font-serif text-white">Upload Your Photos</h2>
              </div>
              <p className="text-center mt-2 text-sage-100">
                Help us create lasting memories by sharing your photos
              </p>
            </div>
            
            <PhotoUpload eventType={eventType} />
          </FloralCard>

          {/* Instructions */}
          <FloralCard variant="minimal" padding="md" className="mt-6 bg-sage-50" showCornerDecorations={false} showWatercolorBackground={false}>
            <h3 className="font-semibold text-sage-900 mb-3 font-serif">Photo Upload Guidelines:</h3>
            <ul className="text-sm text-sage-800 space-y-2">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-sage-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Upload high-quality photos (JPEG, PNG, or HEIC format)</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-sage-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Maximum file size: 10MB per photo</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-sage-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>You can upload multiple photos at once</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-sage-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Photos will be shared with the couple after the event</span>
              </li>
            </ul>
          </FloralCard>

          {/* Footer */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center text-sage-500 mb-2">
              <Sparkles className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Creating beautiful memories together</span>
              <Sparkles className="w-4 h-4 ml-2" />
            </div>
            <p className="text-xs text-gray-500">
              Photos will be reviewed and added to our wedding gallery
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadPage;